import { Building2, GraduationCap, Megaphone, Handshake, MapPin, Brain, ClipboardList, Headset, MessageCircle, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading, PrimaryButton, SecondaryButton } from "../components/primitives";
import { PageHero, CTASection } from "../components/sections";
import { useLeadModal } from "../components/LeadModalContext";
import { waLink } from "../data/site";
import { track } from "../lib/track";

const WHY_FRANCHISE = [
  { icon: Brain, title: "A Differentiated Brand", text: "Operate under DWC — a confidence-and-psychology driving brand with the 'Where Psychology Meets The Road' positioning that ordinary driving schools cannot copy." },
  { icon: GraduationCap, title: "The 33-Lesson Training System", text: "The complete DWC methodology — Learning DNA, Reaction DNA and Confidence DNA — refined over 14+ years and 10,288+ learners." },
  { icon: MapPin, title: "City Exclusivity", text: "DWC works on a one city – one outlet approach where applicable, confirmed city-by-city during the discovery process." },
  { icon: Megaphone, title: "Marketing Support", text: "Brand marketing support, proven messaging and lead-generation systems built around the appointment booking journey." },
  { icon: ClipboardList, title: "Operational & Instructor Support", text: "Operational playbooks, structured instructor training and quality standards so every session delivers the DWC experience." },
  { icon: Headset, title: "Customer Support System", text: "A dedicated customer support approach — before, during and after training — that keeps learners and families confident." },
];

const PARTNER_GETS = [
  "DWC brand identity and positioning",
  "33-Lesson System and three DNA framework",
  "DPR — Driving Psychology Report methodology",
  "Instructor training and quality standards",
  "Marketing and lead-generation support",
  "Operational playbooks and processes",
  "Ongoing brand and business support",
  "Customer support system",
];

const STEPS = [
  "Book a franchise discovery call",
  "Discussion with the DWC franchise team",
  "City and exclusivity alignment",
  "Investment and business discussion",
  "Agreement and onboarding",
  "Instructor and team training",
  "Launch with DWC support",
];

const Franchise = () => {
  const { open } = useLeadModal();
  return (
    <>
      <PageHero
        eyebrow="DWC Franchise"
        lines={["Bring DWC To Your City.", "Build A Confidence Brand."]}
        text="DWC is open for franchise. Bring India's structured, psychology-based driving training system — and its money-back guarantee model — to your city as an exclusive business partner."
      >
        <PrimaryButton onClick={() => open({ type: "franchise", title: "Book a Franchise Discovery Call" })} testId="franchise-apply-btn">
          Book a Franchise Discovery Call
        </PrimaryButton>
        <SecondaryButton
          href={waLink("Hello DWC! I am interested in the DWC Franchise opportunity. Please connect me with your franchise team.")}
          testId="franchise-whatsapp-btn"
          onClick={() => track("whatsapp_click", { source: "franchise" })}
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" /> Talk to Our Franchise Team
        </SecondaryButton>
      </PageHero>

      <section className="bg-white py-24 md:py-32" data-testid="franchise-vision-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            eyebrow="The Franchise Vision"
            number="01"
            title="Most Driving Schools Compete On Price. DWC Competes On Transformation."
            text="India has thousands of driving schools, but very few structured driving-confidence systems. The DWC franchise brings a differentiated, psychology-led training brand to your city — built on 14+ years of experience, a 33-lesson methodology and genuine student outcomes."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_FRANCHISE.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08} className="h-full">
                <article data-testid={`franchise-pillar-${i}`} className="h-full rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green">
                    <p.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-lg font-extrabold tracking-tight text-dwc-charcoal">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/65">{p.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-dwc-charcoal py-24 md:py-32 grain" data-testid="franchise-gets-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <SectionHeading
            dark
            eyebrow="What The Partner Gets"
            number="02"
            title="A Complete Business System, Not Just A Name."
            text="The DWC franchise is a full training-and-business system — brand, methodology, marketing, operations and support."
          />
          <Reveal delay={0.15}>
            <ul className="grid gap-3 sm:grid-cols-2">
              {PARTNER_GETS.map((g) => (
                <li key={g} className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/5 p-4 text-sm font-semibold text-white/85">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" aria-hidden="true" />
                  {g}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-dwc-soft py-24 md:py-32" data-testid="franchise-investment-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading
              eyebrow="Investment & Business"
              number="03"
              title="Investment, Relationship & Business Potential"
              text="DWC believes in a transparent, long-term franchise relationship. Investment details, city exclusivity terms and realistic business expectations are discussed directly with the franchise team during the discovery call — based on your city, scale and plan. We share real numbers in a real conversation, not on a webpage."
            />
            <Reveal delay={0.15}>
              <div className="mt-8 flex items-center gap-3 rounded-2xl border border-dwc-green/15 bg-white p-5">
                <Building2 className="h-5 w-5 shrink-0 text-dwc-crimson" aria-hidden="true" />
                <p className="text-sm font-semibold text-dwc-charcoal/75">City availability and exclusivity are confirmed city-by-city during the discovery process.</p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-dwc-green/15 bg-white p-5">
                <Handshake className="h-5 w-5 shrink-0 text-dwc-green" aria-hidden="true" />
                <p className="text-sm font-semibold text-dwc-charcoal/75">The franchise relationship includes ongoing training, marketing, operational and brand support from the DWC team.</p>
              </div>
            </Reveal>
          </div>
          <div>
            <SectionHeading eyebrow="How To Get Started" number="04" title="Your Path To A DWC Franchise" />
            <ol className="mt-10 space-y-4">
              {STEPS.map((s, i) => (
                <Reveal key={s} delay={i * 0.06}>
                  <li className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-[0_4px_16px_rgb(0,0,0,0.03)]">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-dwc-green text-xs font-black text-white">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm font-bold text-dwc-charcoal/80">{s}</span>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <CTASection
        title="Bring DWC To Your City."
        sub="Book a franchise discovery call and the DWC team will walk you through the brand, the system, city exclusivity, investment and support in detail."
        chapter="05"
      />
    </>
  );
};

export default Franchise;
